import { ActionIcon, Box, Card, Checkbox, Group, Loader, Menu, Table, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconArrowRight, IconDotsVertical, IconInbox, IconRefresh } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { SYNC_STATUS } from '../../domain/status.js';
import { usePullingEnvironments } from '../../hooks/usePullingEnvironments.js';
import { SectionHeading } from '../SectionHeading/SectionHeading.js';
import { glyphIcon } from '../StatusBadge/glyphs.js';
import { columnHeaderView, HEADER_STATUSES, type HeaderRow } from './columnHeaderView.js';
import type { EnvironmentHandlers, MatrixColumn } from './types.js';

/**
 * The frame both grids share: the card, the toolbar slot, the horizontally scrolling box,
 * the sticky header well and one environment column per registered environment.
 *
 * There are two grids — studios on the Studios page, games on the Games page — and each
 * holds ONE kind of row, which is the whole point of the split. What
 * they still have in common is everything here: the same card surface and hairline, the same
 * recessed header well, the same sticky key column, the same column header with its counts
 * and its Pull / Push / Inbox menu. Keeping it in one file is what stops the two drifting apart.
 *
 * NOT a `useCustomTable` surface: the columns come from the environment registry at runtime,
 * the cells carry a state rather than a value, and the primary gesture is "select across
 * rows, then push".
 */

export type MatrixCardProps = {
    /** The rows on screen: a column header's glyphs count their drift and missing in that column, nothing else. */
    rows: readonly HeaderRow[];
    /** The sticky key column's header — the singular label of the rows below it. */
    label: string;
    columns: MatrixColumn[];
    /** The grid's own toolbar (`GridToolbar`) — the first row inside the card. */
    toolbar: ReactNode;
    /** An extra sticky-adjacent header cell between the key column and the environment columns — the Studios grid's Games count. */
    extraHeader?: ReactNode;
    /** The column kebab's Pull now · Push all drift & missing · Inbox — none needs a selection. */
    onEnvironment: EnvironmentHandlers;
    selectAll: { checked: boolean; indeterminate: boolean; onChange: (checked: boolean) => void; ariaLabel: string };
    /** The `<Table.Tr>` rows. */
    children: ReactNode;
};

export const MatrixCard = ({
    rows,
    label,
    columns,
    toolbar,
    extraHeader,
    onEnvironment,
    selectAll,
    children,
}: MatrixCardProps) => {
    const pulling = usePullingEnvironments();

    return (
        <Card p={0} style={{ overflow: 'hidden' }}>
            {toolbar}
            {/* The only horizontally scrolling thing on the page, and it keeps the key column
                pinned so the row label survives the scroll. */}
            <Box style={{ overflowX: 'auto' }}>
                <Table highlightOnHover verticalSpacing={6} horizontalSpacing="sm" style={{ minWidth: 720 }}>
                    <Table.Thead style={{ background: 'var(--rgs-surface-2)' }}>
                        <Table.Tr>
                            <Table.Th style={stickyHead(0)} w={40}>
                                <Checkbox
                                    aria-label={selectAll.ariaLabel}
                                    checked={selectAll.checked}
                                    indeterminate={selectAll.indeterminate}
                                    onChange={(event) => selectAll.onChange(event.currentTarget.checked)}
                                />
                            </Table.Th>
                            <Table.Th style={stickyHead(40)}>
                                <SectionHeading variant="column">{label}</SectionHeading>
                            </Table.Th>
                            {extraHeader}
                            {columns.map((column) => (
                                <Table.Th key={column.name} style={{ minWidth: 132 }}>
                                    <ColumnHeader
                                        rows={rows}
                                        column={column}
                                        onEnvironment={onEnvironment}
                                        pulling={pulling.has(column.name)}
                                    />
                                </Table.Th>
                            ))}
                        </Table.Tr>
                    </Table.Thead>

                    <Table.Tbody>{children}</Table.Tbody>
                </Table>
            </Box>
        </Card>
    );
};

/**
 * The column header is one line: environment name, a pull spinner or a failed-pull triangle,
 * the drift / missing counts of the rows on screen in this column as glyph + number in the
 * status ink, then the kebab. The kebab's Push and Inbox are environment-wide, so their counts
 * are the last pull's. It also carries the environment-level gestures that need no selection
 * at all: *Pull `<env>` now*, *Push all drift and missing* and *Inbox*. Both grids get it, so
 * an environment action is in the same place whichever screen you are on.
 */
const ColumnHeader = ({
    rows,
    column,
    onEnvironment,
    pulling,
}: {
    rows: readonly HeaderRow[];
    column: MatrixColumn;
    onEnvironment: EnvironmentHandlers;
    pulling: boolean;
}) => {
    const { actionable, inboxCount, nothingToPush, shown } = columnHeaderView(column, rows);

    return (
        <Group gap={8} wrap="nowrap">
            <Text className="rgs-numeric" fz="xs" fw={600}>
                {column.name}
            </Text>
            {pulling && <Loader size={12} />}
            {column.pullFailed && (
                <Tooltip label={column.pullError ?? 'The last pull failed'} withArrow multiline w={280}>
                    <IconAlertTriangle
                        size={14}
                        stroke={1.7}
                        color="var(--mantine-color-red-filled)"
                        aria-label="Pull failed"
                    />
                </Tooltip>
            )}

            {shown ? (
                <Group gap={6} wrap="nowrap">
                    {HEADER_STATUSES.filter((s) => shown[s] > 0).map((s) => {
                        const Glyph = glyphIcon(SYNC_STATUS[s].glyph);
                        return (
                            <Tooltip key={s} label={`${shown[s]} ${SYNC_STATUS[s].label.toLowerCase()}`} withArrow>
                                <Group
                                    gap={2}
                                    wrap="nowrap"
                                    c={SYNC_STATUS[s].color}
                                    role="img"
                                    aria-label={`${shown[s]} ${SYNC_STATUS[s].label.toLowerCase()}`}
                                >
                                    <Glyph size={11} stroke={2.6} />
                                    <Text fz={11} fw={600} className="rgs-numeric" c="inherit">
                                        {shown[s]}
                                    </Text>
                                </Group>
                            </Tooltip>
                        );
                    })}
                </Group>
            ) : (
                <Text fz={10} c="dimmed">
                    never pulled
                </Text>
            )}

            <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        ml="auto"
                        aria-label={`Actions for ${column.name}`}
                    >
                        <IconDotsVertical size={14} stroke={1.7} />
                    </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        leftSection={<IconRefresh size={14} stroke={1.7} />}
                        disabled={pulling}
                        onClick={() => onEnvironment.pull(column.name)}
                    >
                        {pulling ? (
                            'Pulling…'
                        ) : (
                            <>
                                Pull{' '}
                                <Text span className="rgs-numeric">
                                    {column.name}
                                </Text>{' '}
                                now
                            </>
                        )}
                    </Menu.Item>
                    <Menu.Divider />
                    {nothingToPush ? (
                        <Menu.Item leftSection={<IconArrowRight size={14} stroke={1.7} />} disabled>
                            Nothing to push
                        </Menu.Item>
                    ) : (
                        <Menu.Item
                            leftSection={<IconArrowRight size={14} stroke={1.7} />}
                            onClick={() => onEnvironment.push(column.name)}
                        >
                            Push all drift & missing ({actionable})
                        </Menu.Item>
                    )}
                    <Menu.Item
                        leftSection={<IconInbox size={14} stroke={1.7} />}
                        disabled={inboxCount === 0}
                        onClick={() => onEnvironment.inbox(column.name)}
                    >
                        Inbox ({inboxCount})
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        </Group>
    );
};

const stickyHead = (left: number): React.CSSProperties => ({
    position: 'sticky',
    left,
    zIndex: 2,
    background: 'var(--rgs-surface-2)',
});
